import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";
import { API, centralISO, centralParts } from "../../lib/eventUtils";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">{label}</span>
    {children}
  </label>
);

export const EventForm = ({ event, defaults, onSaved, onCancel }) => {
  const parts = event ? centralParts(event.date_start) : { date: "", time: "08:00" };
  const [f, setF] = useState({
    title: event?.title || "",
    partner: event?.partner || defaults?.partner || "Dallas Performance Systems",
    slug: event?.slug || "",
    led_by: event?.led_by || "",
    date: parts.date,
    time: parts.time,
    duration_minutes: event?.duration_minutes ?? 60,
    capacity: event?.capacity ?? defaults?.capacity ?? 24,
    price: event?.price_cents ? (event.price_cents / 100).toString() : "0",
    location_name: event?.location_name || "",
    location_address: event?.location_address || "",
    description: event?.description || "",
    backdrop_video: event?.backdrop_video || "",
    backdrop_poster: event?.backdrop_poster || "",
    notes: (event?.notes || []).join("\n"),
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.date || !f.time) {
      toast.error("Set the event date and time");
      return;
    }
    setBusy(true);
    const payload = {
      title: f.title,
      partner: f.partner,
      description: f.description,
      date_start: centralISO(f.date, f.time),
      duration_minutes: Number(f.duration_minutes) || 60,
      led_by: f.led_by,
      location_name: f.location_name,
      location_address: f.location_address,
      capacity: Number(f.capacity) || 1,
      price_cents: Math.round((parseFloat(f.price) || 0) * 100),
      backdrop_video: f.backdrop_video || null,
      backdrop_poster: f.backdrop_poster || null,
      notes: f.notes.split("\n").map((n) => n.trim()).filter(Boolean),
    };
    if (f.slug) payload.slug = f.slug;
    try {
      if (event) {
        await axios.patch(`${API}/api/admin/events/${event.event_id}`, payload, { withCredentials: true });
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/api/admin/events`, payload, { withCredentials: true });
        toast.success("Event created — it starts unpublished");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-volt/40 bg-[#0c0c0c] p-6 sm:p-8 mb-8" data-testid="admin-event-form">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-head font-extrabold uppercase text-2xl tracking-tight">{event ? `Edit: ${event.title}` : "New Event"}</h2>
        <button type="button" onClick={onCancel} className="text-zinc-500 hover:text-white transition-colors" data-testid="event-form-close" aria-label="Close form">
          <X size={20} />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <Field label="Title *"><input required value={f.title} onChange={set("title")} className="volt-input" data-testid="event-form-title" /></Field>
        <Field label="Partner *"><input required value={f.partner} onChange={set("partner")} className="volt-input" data-testid="event-form-partner" /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <Field label="URL Slug"><input value={f.slug} onChange={set("slug")} placeholder="auto-generated from title" className="volt-input" data-testid="event-form-slug" /></Field>
        <Field label="Led By"><input value={f.led_by} onChange={set("led_by")} className="volt-input" data-testid="event-form-led-by" /></Field>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-4">
        <Field label="Date (Central) *"><input required type="date" value={f.date} onChange={set("date")} className="volt-input" data-testid="event-form-date" /></Field>
        <Field label="Time (Central) *"><input required type="time" value={f.time} onChange={set("time")} className="volt-input" data-testid="event-form-time" /></Field>
        <Field label="Duration (min)"><input type="number" min="15" value={f.duration_minutes} onChange={set("duration_minutes")} className="volt-input" data-testid="event-form-duration" /></Field>
        <Field label="Capacity"><input type="number" min="1" value={f.capacity} onChange={set("capacity")} className="volt-input" data-testid="event-form-capacity" /></Field>
        <Field label="Price ($ · 0 = free)"><input type="number" min="0" step="0.01" value={f.price} onChange={set("price")} className="volt-input" data-testid="event-form-price" /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <Field label="Location Name"><input value={f.location_name} onChange={set("location_name")} className="volt-input" data-testid="event-form-location-name" /></Field>
        <Field label="Location Address"><input value={f.location_address} onChange={set("location_address")} className="volt-input" data-testid="event-form-location-address" /></Field>
      </div>
      <div className="mb-4">
        <Field label="Description"><textarea rows={4} value={f.description} onChange={set("description")} className="volt-input" data-testid="event-form-description" /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <Field label="Backdrop Video URL"><input value={f.backdrop_video} onChange={set("backdrop_video")} className="volt-input" data-testid="event-form-backdrop-video" /></Field>
        <Field label="Backdrop Poster URL"><input value={f.backdrop_poster} onChange={set("backdrop_poster")} className="volt-input" data-testid="event-form-backdrop-poster" /></Field>
      </div>
      <div className="mb-6">
        <Field label="Notes (one per line)"><textarea rows={3} value={f.notes} onChange={set("notes")} placeholder={"Bring water\nArrive by 7:50 AM"} className="volt-input" data-testid="event-form-notes" /></Field>
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={busy} className="btn-volt disabled:opacity-60" data-testid="event-form-save">
          {busy ? "Saving…" : event ? "Save Changes" : "Create Event"}
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost" data-testid="event-form-cancel">Cancel</button>
      </div>
    </form>
  );
};
